import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import {
  FiCreditCard,
  FiEdit2,
  FiTrash2,
  FiUserCheck,
  FiUserX,
} from "react-icons/fi";
import Modal from "../components/ui/Modal";
import UserView from "../components/user/UserView";
import UserEdit from "../components/user/UserEdit";
import UserCredits from "../components/user/UserCredits";
import {
  useGetUserByIdQuery,
  useGetUserQuery,
  useResyncUserMutation,
  useUpdateUserMutation,
} from "../apis/user";
import moment from "moment-timezone";
import Button from "components/ui/Button";
import Table from "components/ui/Table";
import SearchSelect from "components/ui/SearchSelect";
import OrganizationFilter from "components/organization/OrganizationFilter";

const AGENT_TYPE_OPTIONS = [
  { value: "", label: "All Agent Types" },
  { value: "mainAgent", label: "Main Agent" },
  { value: "coAgent", label: "Co-Agent" },
];

const LICENSE_VALIDITY_OPTIONS = [
  { value: "", label: "All License Status" },
  { value: "true", label: "Valid Licence" },
  { value: "false", label: "Invalid Licence" },
];

const getOption = (options, value) =>
  options.find((o) => o.value === value) || null;

const Users = () => {
  const [viewMode, setViewMode] = useState("active"); // "active" or "deleted"
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isVisibilityOpen, setIsVisibilityOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isCreditsOpen, setIsCreditsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [agentTypeFilter, setAgentTypeFilter] = useState("");
  const [licenseValidFilter, setLicenseValidFilter] = useState("");
  // Table page size
  const pageSize = 10;

  const { isPending, isError, user, error } = useGetUserByIdQuery(selectedId);
  const { mutate: updateUser, isLoading: isUserLoading } =
    useUpdateUserMutation();
  const { mutate: resyncUser, isLoading: isResyncLoading } =
    useResyncUserMutation();

  const [organizationId, setOrganizationId] = useState("");
  const userQueryParams = useMemo(() => {
    const params = {};
    if (agentTypeFilter) params.agentType = agentTypeFilter;
    if (licenseValidFilter) params["license.isValid"] = licenseValidFilter;
    if (organizationId) params.organizationId = organizationId;
    return params;
  }, [agentTypeFilter, licenseValidFilter, organizationId]);

  const { data, isLoading } = useGetUserQuery(userQueryParams);

  // Filter data based on viewMode
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (viewMode === "active") {
      return data.filter((user) => !user.isDeleted);
    } else {
      return data.filter((user) => user.isDeleted);
    }
  }, [data, viewMode]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "email",
        header: "Email",
        isFilter: true,
      },
      {
        accessorKey: "mobile",
        header: "Mobile",
        isFilter: true,
      },
      {
        accessorKey: "agentType",
        header: "Role",
        cell: (row) => (row.agentType === "coAgent" ? "Co-Agent" : "Main Agent"),
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "mainAgent.name",
        header: "Works for / Team",
        minWidth: "200px",
        cell: (row) => {
          if (row.agentType === "coAgent") {
            return row.mainAgent ? (
              <p>Works for {row.mainAgent.name}</p>
            ) : (
              <p className="text-red-500">No main agent</p>
            );
          }
          return row.team?.length ? (
            <p>
              {row.team.length} co-agent{row.team.length === 1 ? "" : "s"}:{" "}
              {row.team.map((member) => member.name).join(", ")}
            </p>
          ) : (
            <p>—</p>
          );
        },
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "organization.name",
        header: "Organization",
        cell: (row) => <p>{row.organization?.name ?? "—"}</p>,
        isFilter: true,
      },
      {
        accessorKey: "createdAt",
        header: "Joining Date",
        cell: (row) => (
          <p>
            {moment(row.createdAt).tz("Australia/Sydney").format("D MMM YYYY")}
          </p>
        ),
        isFilter: false,
      },
      ...(viewMode === "deleted"
        ? [
            {
              accessorKey: "deletedAt",
              header: "Deleted Date",
              cell: (row) => (
                <p>
                  {row.deletedAt
                    ? moment(row.deletedAt)
                        .tz("Australia/Sydney")
                        .format("D MMM YYYY")
                    : "-"}
                </p>
              ),
              isFilter: false,
            },
          ]
        : []),
      {
        accessorKey: "license.isValid",
        header: "Valid Licence",
        cell: (row) => (row.license?.isValid ? "Yes" : "No"),
        isFilter: false,
        isSortable: false,
      },
      ...(viewMode === "active"
        ? [
            {
              accessorKey: "isVisible",
              header: "Website Status",
              cell: (row) => (
                <button
                  onClick={() => {
                    setSelectedId(row._id);
                    setIsVisibilityOpen(true);
                  }}
                  className={`px-3 py-1 rounded text-sm ${
                    row.isVisible
                      ? "bg-green-100 text-green-600"
                      : "bg-mediumgray"
                  }`}
                >
                  {row.isVisible ? "Visible" : "Hidden"}
                </button>
              ),
              isFilter: false,
              isSortable: false,
              stopPropagation: true,
            },
          ]
        : []),
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit"
            >
              <FiEdit2 fontSize={20} />
            </button>
            {viewMode === "active" && (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsCreditsOpen(true);
                }}
                className="edit-icon"
                title="Credits"
              >
                <FiCreditCard fontSize={20} />
              </button>
            )}
            {viewMode === "active" ? (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsDeleteOpen(true);
                }}
                className="delete-icon"
                title="Delete"
              >
                <FiTrash2 fontSize={20} />
              </button>
            ) : (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsRestoreOpen(true);
                }}
                className="view-icon"
                title="Restore User"
              >
                <FiUserCheck fontSize={20} color="green" />
              </button>
            )}
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "140px",
      },
    ],
    [viewMode],
  );

  if (isLoading) {
    return <PageLoader />;
  }

  const getSelectedUser = (id = selectedId) => data?.find((u) => u._id === id);

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  return (
    <>
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="User Details"
        className="max-w-2xl"
      >
        <UserView
          user={user}
          isLoading={isPending}
          isError={isError}
          error={error}
        />
      </Modal>

      <Modal
        isOpen={isVisibilityOpen}
        onClose={() => setIsVisibilityOpen(false)}
        title={
          getSelectedUser()?.isVisible
            ? "Hide User from Website"
            : "Show User on Website"
        }
        className="max-w-md"
      >
        <p className="text-center">
          Are you sure you want to{" "}
          {getSelectedUser()?.isVisible ? "hide" : "show"} this user{" "}
          {getSelectedUser()?.isVisible ? "from" : "on"} the website?
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            onClick={() => setIsVisibilityOpen(false)}
            className="gray-button"
          >
            No
          </Button>
          <Button
            className="black-button"
            loading={isUserLoading}
            disabled={isUserLoading}
            onClick={() => {
              const u = getSelectedUser();
              updateUser(
                {
                  ...u,
                  id: selectedId,
                  isVisible: !u.isVisible,
                },
                {
                  onSuccess: () => {
                    setIsVisibilityOpen(false);
                    resyncUser();
                  },
                },
              );
            }}
          >
            Yes
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete User"
        className="max-w-md"
      >
        <p className="text-center">
          Are you sure you want to delete this user?
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            onClick={() => setIsDeleteOpen(false)}
            className="gray-button"
          >
            No
          </Button>
          <Button
            className="black-button"
            loading={isUserLoading}
            disabled={isUserLoading}
            onClick={() => {
              updateUser(
                {
                  ...getSelectedUser(),
                  id: selectedId,
                  isVisible: false,
                  isDeleted: true,
                  deletedAt: new Date().toISOString(),
                },
                {
                  onSuccess: () => {
                    setIsDeleteOpen(false);
                    resyncUser();
                  },
                },
              );
            }}
          >
            Yes
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isRestoreOpen}
        onClose={() => setIsRestoreOpen(false)}
        title="Restore User"
        className="max-w-md"
      >
        <p className="text-center">
          Are you sure you want to restore this user?
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            onClick={() => setIsRestoreOpen(false)}
            className="gray-button"
          >
            No
          </Button>
          <Button
            className="black-button"
            loading={isUserLoading}
            disabled={isUserLoading}
            onClick={() => {
              updateUser(
                {
                  ...getSelectedUser(),
                  id: selectedId,
                  isDeleted: false,
                  deletedAt: null,
                },
                {
                  onSuccess: () => {
                    setIsRestoreOpen(false);
                    resyncUser();
                  },
                },
              );
            }}
          >
            Yes
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isCreditsOpen}
        onClose={() => setIsCreditsOpen(false)}
        title="Credits"
        className="max-w-lg"
      >
        <UserCredits
          user={getSelectedUser()}
          onClose={() => setIsCreditsOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        className="max-w-2xl"
      >
        <UserEdit
          name={getSelectedUser()?.name}
          picture={getSelectedUser()?.picture}
          mobile={getSelectedUser()?.mobile}
          title={getSelectedUser()?.title}
          suburbs={getSelectedUser()?.suburbs}
          facebookUrl={getSelectedUser()?.facebookUrl}
          instagramUrl={getSelectedUser()?.instagramUrl}
          userData={getSelectedUser()}
          updateUser={updateUser}
          resyncUser={resyncUser}
          isLoading={isUserLoading}
          selectedId={selectedId}
          setIsEditOpen={setIsEditOpen}
        />
      </Modal>

      <div className="container mx-auto">
        {/* Toggle between Active and Deleted Users */}
        <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
          <div className="flex gap-2 bg-lightgray rounded-lg p-1 border border-mediumgray">
            <button
              onClick={() => setViewMode("active")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "active"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiUserCheck className="w-4 h-4" />
              <span>Active Users</span>
            </button>
            <button
              onClick={() => setViewMode("deleted")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "deleted"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiUserX className="w-4 h-4" />
              <span>Deleted Users</span>
            </button>
          </div>

          <div className="flex gap-3">
            <div className="">
              <OrganizationFilter
                value={organizationId}
                onChange={setOrganizationId}
                label=""
              />
            </div>
            <div className="">
              <SearchSelect
                options={AGENT_TYPE_OPTIONS}
                value={getOption(AGENT_TYPE_OPTIONS, agentTypeFilter)}
                onChange={(opt) => setAgentTypeFilter(opt?.value ?? "")}
                placeholder="All Agent Types"
                searchable={false}
              />
            </div>
            <div className="">
              <SearchSelect
                options={LICENSE_VALIDITY_OPTIONS}
                value={getOption(LICENSE_VALIDITY_OPTIONS, licenseValidFilter)}
                onChange={(opt) => setLicenseValidFilter(opt?.value ?? "")}
                placeholder="All License Status"
                searchable={false}
              />
            </div>
          </div>
        </div>

        <Table
          title="Users"
          columns={columns}
          data={filteredData}
          pageSize={pageSize}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Users;
